import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";

import { Button, Label } from "@fronterahealth/frontera-ui-components";

import { listLearnerTestProfiles, startPromptTestRun } from "@api/rest/prompt-test-bed";
import {
  createEvalModelComparison,
  getListEvalModelComparisonsQueryKey,
  useListAvailableModels,
  useListTemplates,
  usePreviewEvalModelComparison,
} from "@api/rest/types-and-hooks";
import { Arm, type ArmValue, PageLocation } from "@components/ModelTesting/constants";
import { notifyError, notifyWarn } from "@components/notifications/notifications";
import { REPORT_TYPE_LABELS, ReportType, TemplateStatus } from "@pages/Templates/constants";

const SELECT_CLASSES =
  "w-full text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-default " +
  "focus:outline-none focus:ring-1 focus:ring-limestone-900 disabled:opacity-40";

interface Props {
  orgs: { id: string | number; name: string }[];
  orgId: number | null;
  /** Owned by the tab — it also filters the past-comparisons list. */
  onOrgChange: (orgId: number | null) => void;
  onStarted: (comparisonId: number) => void;
}

/** Kickoff: template → profile → candidate model → run. The POST creates the
 *  slots; runs fan out from here per field per arm — a failed start leaves
 *  its slot pending, restartable from the comparison screen. */
export const ComparisonSetupCard: FC<Props> = ({ orgs, orgId, onOrgChange, onStarted }) => {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [candidateModel, setCandidateModel] = useState<string>("");
  const [starting, setStarting] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  const { data: modelsResponse } = useListAvailableModels();
  const models = modelsResponse?.status === 200 ? modelsResponse.data : null;
  const candidates = useMemo(
    () => (models?.models ?? []).filter((m) => !m.is_default).sort((a, b) => a.label.localeCompare(b.label)),
    [models],
  );

  // With an org: its templates. Without: base templates only — enterprise ones are reached through their org.
  const { data: templatesResponse } = useListTemplates(
    orgId !== null ? { organization_id: orgId, page_size: 100 } : { is_base: true, page_size: 100 },
  );
  // Published only — the comparison must run what production runs.
  const templates = ((templatesResponse?.status === 200 ? templatesResponse.data.results : null) ?? [])
    .filter((t) => t.status === TemplateStatus.PUBLISHED)
    .sort((a, b) => a.name.localeCompare(b.name));

  // The server says what a kickoff would run — the same gate create uses;
  // no client-side mirror.
  const { data: previewResponse } = usePreviewEvalModelComparison(
    { template_id: templateId ?? 0, learner_test_profile_id: profileId ?? 0 },
    { query: { enabled: templateId !== null && profileId !== null } },
  );
  const preview = previewResponse?.status === 200 ? previewResponse.data : null;
  const runnableCount = preview?.fields?.length ?? null;
  const skippedFields = preview?.skipped_fields ?? [];

  // No org → the global profiles (org_id null returns universals).
  const { data: profiles = [] } = useQuery({
    queryKey: ["learner-test-profiles", orgId],
    queryFn: () => listLearnerTestProfiles({ org_id: orgId }),
    select: (rows) => [...rows].sort((a, b) => a.name.localeCompare(b.name)),
  });

  const ready =
    templateId !== null && profileId !== null && candidateModel !== "" && (runnableCount === null || runnableCount > 0);

  const handleStart = async () => {
    if (!ready || templateId === null || profileId === null) return;
    setStarting(true);
    try {
      const resp = await createEvalModelComparison({
        // Omitted org = a global comparison (base/enterprise template).
        ...(orgId !== null ? { organization_id: orgId } : {}),
        template_id: templateId,
        learner_test_profile_id: profileId,
        candidate_model: candidateModel,
      });
      if (resp.status !== 200) {
        notifyError((resp.data as { detail?: string })?.detail ?? "Could not create the comparison.");
        return;
      }
      const comparison = resp.data;

      // Baseline model sent explicitly so the run records what it was asked
      // for, not the pipeline default of the moment.
      const arms: { arm: ArmValue; model: string }[] = [
        { arm: Arm.Baseline, model: comparison.baseline_model },
        { arm: Arm.Candidate, model: comparison.candidate_model },
      ];
      // An older backend may not send these arrays — degrade, don't crash.
      const allSlots = comparison.fields ?? [];
      const skippedFields = comparison.skipped_fields ?? [];
      // DIAGNOSIS_EVALUATION conclusions fields run LAST: their prompts
      // consume the other sections' outputs, so the server gates them.
      // The comparison screen auto-starts them once each arm's sections
      // complete.
      const gated = comparison.report_type === ReportType.DIAGNOSIS_EVALUATION;
      const slots = gated ? allSlots.filter((s) => s.page_location !== PageLocation.Conclusions) : allSlots;
      const results = await Promise.all(
        slots.flatMap((slot) =>
          arms.map(({ arm, model }) =>
            startPromptTestRun(slot.prompt_key, {
              learner_test_profile_id: profileId,
              model,
              comparison_id: comparison.id,
              arm,
              use_draft: false,
            }).catch(() => null),
          ),
        ),
      );
      const failedStarts = results.filter((r) => r === null).length;
      if (failedStarts > 0) {
        notifyError(`${failedStarts} of ${results.length} runs failed to start. Start them again from the comparison.`);
      }
      if (skippedFields.length > 0) {
        const kinds = [...new Set(skippedFields.flatMap((f) => f.missing_file_kinds ?? []))].join(", ");
        notifyWarn(
          `${skippedFields.length} field(s) skipped — the profile has no matching assessment file (${kinds}).`,
        );
      }
      // The landing list is mounted behind this card — invalidate or the new row is missing.
      await queryClient.invalidateQueries({ queryKey: getListEvalModelComparisonsQueryKey() });
      onStarted(comparison.id);
    } catch {
      // A network-level rejection never reaches the status branches.
      notifyError("Could not start the comparison — check your connection.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
      <Label size="small" colorType="secondary" className="uppercase tracking-wide">
        New comparison
      </Label>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Organization</span>
          <select
            value={orgId?.toString() ?? ""}
            onChange={(e) => {
              onOrgChange(e.target.value ? Number(e.target.value) : null);
              // Downstream is org-scoped — selections must not survive the switch.
              setTemplateId(null);
              setProfileId(null);
            }}
            className={SELECT_CLASSES}
          >
            <option value="">Global — base templates</option>
            {[...(orgs ?? [])]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org) => (
                <option key={org.id} value={String(org.id)}>
                  {org.name}
                </option>
              ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Template</span>
          <select
            value={templateId?.toString() ?? ""}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
            className={SELECT_CLASSES}
          >
            <option value="">Select a template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                #{t.id} — {t.name} —{" "}
                {REPORT_TYPE_LABELS[t.report_type as keyof typeof REPORT_TYPE_LABELS] ?? t.report_type}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Learner profile</span>
          <select
            value={profileId?.toString() ?? ""}
            onChange={(e) => setProfileId(e.target.value ? Number(e.target.value) : null)}
            className={SELECT_CLASSES}
          >
            <option value="">Select a profile…</option>
            {(profiles ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.organization_id === null ? " — global" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Candidate model</span>
          <select value={candidateModel} onChange={(e) => setCandidateModel(e.target.value)} className={SELECT_CLASSES}>
            <option value="">Select a model…</option>
            {(candidates ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <Button
          text={starting ? "Starting…" : "Run comparison"}
          appearance="primary"
          className="rounded-sm"
          disabled={!ready || starting}
          onClick={() => void handleStart()}
        />
        {templateId === null || profileId === null ? null : runnableCount === 0 ? (
          <span className="text-xs text-text-error-primary">
            None of this template&apos;s AI fields have a matching assessment file on this profile.
          </span>
        ) : skippedFields.length > 0 ? (
          // The counts are the headline; the file kinds live behind a toggle.
          <span className="flex items-center gap-2 text-xs text-text-secondary">
            <span>
              {runnableCount} of {(runnableCount ?? 0) + skippedFields.length} fields will run · {skippedFields.length}{" "}
              skipped (profile is missing their files)
            </span>
            <button
              type="button"
              onClick={() => setShowSkipped((v) => !v)}
              className="rounded-md border border-border-medium bg-bg-surface px-2 py-0.5 text-xs font-medium text-text-secondary shadow-sm transition-colors hover:bg-bg-default"
            >
              {showSkipped ? "Hide files" : "Which files?"}
            </button>
          </span>
        ) : null}
      </div>

      {showSkipped && skippedFields.length > 0 ? (
        <div className="mt-3 rounded-md border border-border-subtle bg-bg-default p-3">
          <span className="text-xs font-medium text-text-secondary">No file on this profile for:</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {[...new Set(skippedFields.flatMap((slot) => slot.missing_file_kinds ?? []))].sort().map((kind) => (
              <code
                key={kind}
                className="rounded border border-border-subtle bg-bg-surface px-1.5 py-0.5 font-mono text-xs text-text-primary"
              >
                {kind}
              </code>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};
